import { debounce, isEqual } from "lodash";
import * as React from "react";
import { findDOMNode } from "react-dom";
import styled from "styled-components";

import { DocParagraph } from "../../../data/tos";
import { colors, viewports } from "../../styles/variables";
import { rAFAnimate } from "../../utils/rAFAnimate";

interface Props {
  title: string;
  paragraphs: DocParagraph[];
  read: boolean;

  onPositionUpdate: (topPosition: number, bottomPosition: number) => void;
}

export default class DocumentItem extends React.Component<Props, {}> {
  private isAnimatingScroll = false;

  private scrollContainerRef: any;

  handleScroll = () => {
    if (this.isAnimatingScroll) {
      return;
    }
    this.isAnimatingScroll = true;
    // this.scrollContainerRef.style.pointerEvents = "none";

    const distanceInnerCenterToViewportCenter = Math.round(
      (this.scrollContainerRef.scrollWidth - this.scrollContainerRef.getBoundingClientRect().width) / 2
    );
    const initialPosition = this.scrollContainerRef.scrollLeft;

    let newPosition: number;
    if (this.scrollContainerRef.scrollLeft >= distanceInnerCenterToViewportCenter) {
      newPosition = this.scrollContainerRef.scrollWidth / 2;
    } else {
      newPosition = 0;
    }

    const anim = (percentage: number) => {
      this.scrollContainerRef.scrollLeft = initialPosition + (newPosition - initialPosition) * percentage;
    };
    const end = () => {
      this.isAnimatingScroll = false;
      // this.scrollContainerRef.style.pointerEvents = "auto";
    };
    rAFAnimate(anim, undefined, end, 0.6);
  };

  // tslint:disable-next-line:member-ordering
  debouncedHandleScroll = debounce(this.handleScroll, 150);
  // tslint:disable-next-line:member-ordering
  debouncedHandleScroll2 = debounce(this.handleScroll, 350);

  componentDidMount() {
    this.updatePositionItem();

    // this.scrollContainerRef.addEventListener("scroll", this.debouncedHandleScroll);
    // this.scrollContainerRef.addEventListener("touchend", this.debouncedHandleScroll2);

    window.addEventListener("resize", this.updatePositionItem);
  }

  componentWillUnmount() {
    // this.scrollContainerRef.removeEventListener("scroll", this.debouncedHandleScroll.cancel);
    // this.scrollContainerRef.removeEventListener("touchend", this.debouncedHandleScroll2);
    window.removeEventListener("resize", this.updatePositionItem);
  }

  shouldComponentUpdate(nextProps: Props) {
    // if (nextProps.paragraphs === this.props.paragraphs && nextProps.read === this.props.read && nextProps.title === this.props.title) {
    //   return false;
    // }
    if (isEqual(nextProps, this.props)) {
      return false;
    } else {
      return true;
    }
    // return true;
  }

  render() {
    return (
      <div>
        <StyledItemTitle>
          {this.props.title}
          {this.props.read && <ReadLabel>Lu</ReadLabel>}
        </StyledItemTitle>

        <StyledParagraphContainer ref={(ref: any) => (this.scrollContainerRef = ref)}>
          <FirstBackground>
            <LegalLabel>Legal</LegalLabel>
          </FirstBackground>
          <SecondBackground>
            <AnnotationLabel>En deux mots</AnnotationLabel>
          </SecondBackground>
          {this.props.paragraphs.map((p, i) => (
            <StyledParagraph key={i}>
              <StyledParagraphContent key="leg" dangerouslySetInnerHTML={{ __html: p.legal.replace(/\n/g, "<br>") }} />
              <StyledParagraphContent key="ann" dangerouslySetInnerHTML={{ __html: p.annotation.replace(/\n/g, "<br>") }} />
            </StyledParagraph>
          ))}
        </StyledParagraphContainer>
      </div>
    );
  }

  private updatePositionItem = () => {
    const node: any = findDOMNode(this);
    const rect = node.getBoundingClientRect();
    this.props.onPositionUpdate(rect.top, rect.bottom);
  };
}

const minWidth = 600;
const gutter = 20;
const padd = 20;
const verticalMargin = 20;

const StyledParagraphContainer = styled.div`
  position: relative;
  overflow-x: scroll;
  -ms-overflow-style: -ms-autohiding-scrollbar;
  // -webkit-overflow-scrolling: auto; /* avoid triggering new scroll events after the animation */

  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;

  padding-top: 20px;
  padding-bottom: 20px;

  ${viewports.desktop`
    max-width: 1000px;
    width: calc(100vw - ${2 * padd}px);
    margin: 0 auto;
    left: 0;
    right: 0;
  `};
`;

const StyledParagraph = styled.div`
  position: relative;
  width: ${minWidth}px;
  display: flex;
  margin-top: 25px;
  ${viewports.desktop`
    width: 100%;
  `};
`;

const StyledParagraphContent = styled.div`
  position: relative;
  width: ${minWidth / 2 - gutter / 2}px;
  padding: 20px 20px 20px 20px;

  &:first-child {
    margin-left: ${padd}px;
    margin-right: ${gutter / 2}px;
  }

  &:last-child {
    margin-left: ${gutter / 2}px;
    margin-right: ${padd}px;
  }

  ${viewports.desktop`
    width: calc(50% - ${padd + gutter / 2}px);
  `};
`;

const FirstBackground = styled.div`
  position: absolute;
  left: ${padd}px;
  top: ${verticalMargin}px;
  height: calc(100% - ${verticalMargin * 2}px);
  width: ${minWidth / 2 - (padd + gutter / 2)}px;
  background: white;
  box-shadow: 0 3px 16px rgba(0, 0, 0, 0.16);
  border-radius: 5px;
  ${viewports.desktop`
    width: calc(50% - ${padd + gutter / 2}px);
  `};
`;

const SecondBackground = styled.div`
  position: absolute;
  left: ${minWidth / 2 + gutter / 2}px;
  top: ${verticalMargin};
  height: calc(100% - ${verticalMargin * 2}px);
  width: ${minWidth / 2 - (padd + gutter / 2)}px;
  background: white;
  box-shadow: 0 3px 16px rgba(0, 0, 0, 0.16);
  border-radius: 5px;
  ${viewports.desktop`
    left: calc(50% + ${gutter / 2}px);
    width: calc(50% - ${padd + gutter / 2}px);
  `};
`;

const StyledItemTitle = styled.div`
  text-align: center;
  font-weight: 800;
  font-size: 1.1rem;
  padding: 0.5rem 1rem;
  background: white;
`;

const ReadLabel = styled.div`
  display: inline-block;
  font-size: 0.7rem;
  line-height: 1rem;
  vertical-align: middle;
  margin-left: 5px;
  padding: 2px 6px;
  background: ${colors.green.light};
  color: ${colors.green.dark};
  border-radius: 8px;
  opacity: 0.6;
`;

const LegalLabel = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  padding: 8px 12px;
  background: #b8c5d9;
  font-size: 0.6rem;
  letter-spacing: 0.3px;
  color: white;
  text-transform: uppercase;
  border-radius: 0 5px 0 5px;
`;

const AnnotationLabel = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  padding: 8px 12px;
  background: #b8c5d9;
  font-size: 0.6rem;
  letter-spacing: 0.3px;
  color: white;
  text-transform: uppercase;
  border-radius: 5px 0 5px 0;
`;



// WEBPACK FOOTER //
// ./src/components/structure/DocumentItem.tsx